// src/pages/ClientLinkPage.jsx
// §8bis/§8ter — Écran de consentement du client avant rattachement de son compte
// au dossier tenu par un Art Advisor. Le rattachement exige une ACTION POSITIVE ;
// aucun lien automatique par ressemblance de nom/email. Le client garde le
// contrôle et peut refuser. Lecture/écriture via RPC SECURITY DEFINER. i18n FR/EN/DE.
import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext";
import { ShieldCheck, Package, Image, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";

const COPY = {
  fr: {
    title: "Invitation à retrouver votre collection",
    intro: (a) => `${a || "Votre Art Advisor"} vous invite à relier votre compte ARTKIV au dossier qu’il gère pour vous.`,
    dossier: "Dossier", collections: "collections", artworks: "œuvres",
    whatTitle: "Ce que le rattachement fait",
    whatBody: "En acceptant, votre compte est relié à ce dossier : vous pourrez suivre vos collections, documents et livrables dans votre espace, et collaborer avec votre Advisor. Vous gardez le contrôle des accès et pouvez les modifier ou les révoquer à tout moment. Le rattachement ne partage rien de nouveau sans votre action.",
    accept: "Accepter et relier mon compte", decline: "Refuser",
    accepting: "Rattachement…", declining: "…",
    loginTitle: "Connectez-vous pour continuer",
    loginBody: "Connectez-vous avec l’adresse à laquelle l’invitation a été envoyée pour vérifier votre identité.",
    login: "Se connecter",
    emailMismatch: "Cette invitation a été envoyée à une autre adresse. Connectez-vous avec l’adresse invitée.",
    expired: "Cette invitation a expiré. Demandez à votre Advisor de la renouveler.",
    notPending: "Cette invitation n’est plus valable.",
    notFound: "Invitation introuvable.",
    error: "Une erreur est survenue.",
    acceptedTitle: "Compte relié", acceptedBody: "Votre compte est maintenant relié à ce dossier.",
    declinedTitle: "Invitation refusée", declinedBody: "Vous avez refusé cette invitation. Aucun rattachement n’a été effectué.",
    goToSpace: "Accéder à mon espace", loading: "Chargement…",
    messageLabel: "Message de votre Advisor",
  },
  en: {
    title: "Invitation to access your collection",
    intro: (a) => `${a || "Your Art Advisor"} invites you to link your ARTKIV account to the file they manage for you.`,
    dossier: "File", collections: "collections", artworks: "artworks",
    whatTitle: "What linking does",
    whatBody: "By accepting, your account is linked to this file: you can follow your collections, documents and deliverables in your space and collaborate with your Advisor. You keep control of access and can change or revoke it at any time. Linking shares nothing new without your action.",
    accept: "Accept and link my account", decline: "Decline",
    accepting: "Linking…", declining: "…",
    loginTitle: "Sign in to continue",
    loginBody: "Sign in with the address the invitation was sent to, to verify your identity.",
    login: "Sign in",
    emailMismatch: "This invitation was sent to a different address. Sign in with the invited address.",
    expired: "This invitation has expired. Ask your Advisor to renew it.",
    notPending: "This invitation is no longer valid.",
    notFound: "Invitation not found.",
    error: "Something went wrong.",
    acceptedTitle: "Account linked", acceptedBody: "Your account is now linked to this file.",
    declinedTitle: "Invitation declined", declinedBody: "You declined this invitation. No linking was performed.",
    goToSpace: "Go to my space", loading: "Loading…",
    messageLabel: "Message from your Advisor",
  },
  de: {
    title: "Einladung zu Ihrer Sammlung",
    intro: (a) => `${a || "Ihr Art Advisor"} lädt Sie ein, Ihr ARTKIV-Konto mit der für Sie geführten Akte zu verknüpfen.`,
    dossier: "Akte", collections: "Sammlungen", artworks: "Werke",
    whatTitle: "Was die Verknüpfung bewirkt",
    whatBody: "Mit der Annahme wird Ihr Konto mit dieser Akte verknüpft: Sie können Ihre Sammlungen, Dokumente und Ergebnisse in Ihrem Bereich verfolgen und mit Ihrem Advisor zusammenarbeiten. Sie behalten die Kontrolle über die Zugriffe und können sie jederzeit ändern oder widerrufen. Ohne Ihr Zutun wird nichts Neues geteilt.",
    accept: "Annehmen und Konto verknüpfen", decline: "Ablehnen",
    accepting: "Verknüpfen…", declining: "…",
    loginTitle: "Zum Fortfahren anmelden",
    loginBody: "Melden Sie sich mit der Adresse an, an die die Einladung gesendet wurde, um Ihre Identität zu bestätigen.",
    login: "Anmelden",
    emailMismatch: "Diese Einladung wurde an eine andere Adresse gesendet. Melden Sie sich mit der eingeladenen Adresse an.",
    expired: "Diese Einladung ist abgelaufen. Bitten Sie Ihren Advisor um Erneuerung.",
    notPending: "Diese Einladung ist nicht mehr gültig.",
    notFound: "Einladung nicht gefunden.",
    error: "Ein Fehler ist aufgetreten.",
    acceptedTitle: "Konto verknüpft", acceptedBody: "Ihr Konto ist jetzt mit dieser Akte verknüpft.",
    declinedTitle: "Einladung abgelehnt", declinedBody: "Sie haben diese Einladung abgelehnt. Es wurde keine Verknüpfung vorgenommen.",
    goToSpace: "Zu meinem Bereich", loading: "Laden…",
    messageLabel: "Nachricht Ihres Advisors",
  },
};
const pick = (l) => COPY[String(l || "fr").slice(0, 2).toLowerCase()] || COPY.fr;

const wrap = { minHeight: "100vh", background: "#faf8f4", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: "system-ui, -apple-system, sans-serif" };
const cardS = { width: "100%", maxWidth: 560, background: "#fff", border: "1px solid #e5e3df", borderRadius: 10, padding: 32, boxShadow: "0 1px 3px rgba(26,26,26,0.05)" };

export default function ClientLinkPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const { i18n } = useTranslation();
  const tx = pick(i18n.resolvedLanguage || i18n.language);

  const [loading, setLoading] = useState(true);
  const [inv, setInv] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // 'accepted' | 'declined'

  const errText = useCallback((code) => ({
    email_mismatch: tx.emailMismatch, expired: tx.expired, not_pending: tx.notPending,
    not_found: tx.notFound, not_authenticated: tx.loginBody,
  }[code] || tx.error), [tx]);

  const loadInv = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase.rpc("get_client_invitation", { p_token: token });
    if (error) { setErr(tx.error); setLoading(false); return; }
    if (data?.error) { setErr(errText(data.error)); setLoading(false); return; }
    setInv(data);
    setLoading(false);
  }, [token, tx, errText]);

  useEffect(() => { loadInv(); }, [loadInv]);

  const act = async (fn, resultKey) => {
    setBusy(true); setErr(null);
    const { data, error } = await supabase.rpc(fn, { p_token: token });
    setBusy(false);
    if (error) { setErr(tx.error); return; }
    if (data?.error) { setErr(errText(data.error)); return; }
    setDone(resultKey);
  };

  if (loading) return <div style={wrap}><div style={{ color: "#6b6b6b" }}>{tx.loading}</div></div>;

  if (done) {
    const ok = done === "accepted";
    return (
      <div style={wrap}>
        <div style={cardS}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            {ok ? <CheckCircle2 size={24} color="#2c5f2d" /> : <XCircle size={24} color="#8f3a44" />}
            <h1 style={{ fontFamily: "Georgia, serif", fontSize: 22, fontWeight: 400, color: "#1a1a1a", margin: 0 }}>{ok ? tx.acceptedTitle : tx.declinedTitle}</h1>
          </div>
          <p style={{ fontSize: 14, color: "#6b6b6b", lineHeight: 1.6 }}>{ok ? tx.acceptedBody : tx.declinedBody}</p>
          {ok && (
            <button type="button" onClick={() => navigate("/mon-conseiller")}
              style={{ marginTop: 16, padding: "11px 18px", background: "#1a1a1a", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
              {tx.goToSpace}
            </button>
          )}
        </div>
      </div>
    );
  }

  if (err && !inv) {
    return (
      <div style={wrap}>
        <div style={cardS}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <AlertTriangle size={22} color="#8a6a2f" />
            <h1 style={{ fontFamily: "Georgia, serif", fontSize: 20, fontWeight: 400, color: "#1a1a1a", margin: 0 }}>{tx.title}</h1>
          </div>
          <p style={{ fontSize: 14, color: "#6b6b6b", lineHeight: 1.6 }}>{err}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={wrap}>
      <div style={cardS}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <ShieldCheck size={22} color="#8b7355" />
          <h1 style={{ fontFamily: "Georgia, serif", fontSize: 22, fontWeight: 400, color: "#1a1a1a", margin: 0, letterSpacing: "-0.02em" }}>{tx.title}</h1>
        </div>
        <p style={{ fontSize: 14.5, color: "#4a4a4a", lineHeight: 1.6, marginBottom: 18 }}>{tx.intro(inv?.advisor_name)}</p>

        <div style={{ padding: 16, background: "#faf6ea", border: "1px solid #ecdcae", borderRadius: 8, marginBottom: 16 }}>
          <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: "#8b7355", fontWeight: 700, marginBottom: 6 }}>{tx.dossier}</div>
          <div style={{ fontFamily: "Georgia, serif", fontSize: 18, color: "#1a1a1a" }}>{inv?.entity_name}</div>
          <div style={{ display: "flex", gap: 18, marginTop: 10, fontSize: 13, color: "#6b6b6b" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Package size={15} /> {inv?.collections_count || 0} {tx.collections}</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Image size={15} /> {inv?.artworks_count || 0} {tx.artworks}</span>
          </div>
        </div>

        {inv?.message && (
          <div style={{ marginBottom: 16, fontSize: 13.5, color: "#4a4a4a", lineHeight: 1.6 }}>
            <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: "#9ca3af", fontWeight: 700, marginBottom: 4 }}>{tx.messageLabel}</div>
            {inv.message}
          </div>
        )}

        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 13.5, fontWeight: 600, color: "#1a1a1a", marginBottom: 4 }}>{tx.whatTitle}</div>
          <p style={{ fontSize: 13.5, color: "#6b6b6b", lineHeight: 1.65, margin: 0 }}>{tx.whatBody}</p>
        </div>

        {err && <div style={{ marginBottom: 14, padding: "10px 12px", background: "#f8eef0", border: "1px solid #e6c9cf", borderRadius: 6, fontSize: 13, color: "#8f3a44" }}>{err}</div>}

        {!user ? (
          <div>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: "#1a1a1a", marginBottom: 4 }}>{tx.loginTitle}</div>
            <p style={{ fontSize: 13, color: "#6b6b6b", lineHeight: 1.55, marginBottom: 12 }}>{tx.loginBody}</p>
            <button type="button" onClick={() => navigate(`/login?redirect=${encodeURIComponent(`/client-link/${token}`)}`)}
              style={{ padding: "11px 18px", background: "#1a1a1a", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
              {tx.login}
            </button>
          </div>
        ) : (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button type="button" disabled={busy} onClick={() => act("accept_client_invitation", "accepted")}
              style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "12px 20px", background: "#1a1a1a", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1 }}>
              <CheckCircle2 size={16} /> {busy ? tx.accepting : tx.accept}
            </button>
            <button type="button" disabled={busy} onClick={() => act("decline_client_invitation", "declined")}
              style={{ padding: "12px 18px", background: "#fff", color: "#6b6b6b", border: "1px solid #e5e3df", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: busy ? "default" : "pointer" }}>
              {tx.decline}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
